import type { GenerateContentResponse, ParsedProfileResponse } from '@/api/gql/graphql';
import { ModifySectionType } from '@/api/gql/graphql';
import {
  EVALUATE_CV_MUTATION,
  GENERATE_ALL_CONTENT_MUTATION,
  PARSE_CV_MUTATION,
} from '@/api/onboarding';
import { GOOGLE_LOGIN_MUTATION } from '@/api/profile';
import type { UpdateSectionConfig } from '@/components/UpdateSectionModal';
import { useLocalStorageRef, useLocalStorageState } from '@/hooks/useLocalStorage';
import { usePatchProfil } from '@/hooks/usePatchProfil';
import { profileToFormValues } from '@/utils/profile-form-converter';
import { useMutation } from '@apollo/client';
import type { TemplateViewer } from '@flemme-template/components';
import { templateViews } from '@flemme-template/components';
import type { CredentialResponse } from '@react-oauth/google';
import { useNavigate } from '@tanstack/react-router';
import { Button, notification } from 'antd';
import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { captureException } from '@sentry/core';

export type GenerateContentRequest = {
  jobDescription: string;
  instructions?: string;
};

export type AuthUser = {
  id: string;
  email: string;
  complete_name: string;
  photoUrl?: string;
};

type OnboardingContextType = {
  profileResponse: ParsedProfileResponse | null;
  updateProfile: (profile: ParsedProfileResponse) => void;
  parseCV: (file: File) => Promise<void>;
  evaluateCV: () => Promise<ParsedProfileResponse | null>;
  generateCVContent: (data: GenerateContentRequest) => void;
  cvGenerated: GenerateContentResponse | null;
  handleCvGeneratedChange: (newCv: GenerateContentResponse['cv']) => void;
  rollbackCvGenerated: () => void;
  isLoading: boolean;
  isGenerating: boolean;
  currentMessage: string;
  resolvedRecommendations: string[];
  markRecommendationAsResolved: (recommendationKey: string) => void;
  contentRequest: GenerateContentRequest | null;
  setContentRequest: (request: GenerateContentRequest) => void;
  updateSectionContent: (config: UpdateSectionConfig & { improvement: string }) => void;
  selectedTemplate?: TemplateViewer;
  setSelectedTemplate: (template: TemplateViewer) => void;
  authUser: AuthUser | null;
  login: (user: AuthUser, token: string) => void;
  logout: () => void;
  handleGoogleSuccess: (credentialResponse: CredentialResponse) => void;
};

const OnboardingContext = createContext<OnboardingContextType | null>(null);

export function OnboardingProvider({ children }: { children: React.ReactNode }) {
  const [profile, setProfile] = useLocalStorageState<ParsedProfileResponse | null>(
    'onboarding-profile',
    null
  );
  const [isLoading, setIsLoading] = useState(false);
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [resolvedRecommendations, setResolvedRecommendations] = useLocalStorageState<string[]>(
    'onboarding-resolved-recommendations',
    []
  );
  const [contentRequest, setContentRequest] = useLocalStorageState<GenerateContentRequest | null>(
    'onboarding-content-request',
    null
  );
  const navigate = useNavigate();
  const [selectedTemplateId, setSelectedTemplateId] = useLocalStorageState<string | null>(
    'onboarding-selected-template',
    null
  );
  const [authUser, setAuthUser] = useLocalStorageState<AuthUser | null>('local:user', null);

  const selectedTemplate = useMemo(() => {
    return templateViews.find(template => template.id === selectedTemplateId);
  }, [selectedTemplateId]);

  const setSelectedTemplate = (template: TemplateViewer) => {
    setSelectedTemplateId(template.id);
  };

  const [parseCVMutation] = useMutation(PARSE_CV_MUTATION);
  const [evaluateCVMutation] = useMutation(EVALUATE_CV_MUTATION);
  const { mutate: patchProfile } = usePatchProfil();

  const [generateAllContentMutation, { loading: isGenerating }] = useMutation(
    GENERATE_ALL_CONTENT_MUTATION
  );

  const [cvGenerated, setCvGenerated] = useLocalStorageState<GenerateContentResponse | null>(
    'onboarding-cv-generated',
    null
  );
  const previousCvGeneratedRef = useLocalStorageRef<GenerateContentResponse | null>(
    'onboarding-previous-cv-generated',
    null
  );

  const handleCvGeneratedChange = (newCv: GenerateContentResponse['cv']) => {
    previousCvGeneratedRef.setValue(cvGenerated || null);
    setCvGenerated({
      cv: newCv,
      motivation_letter: {
        content: cvGenerated?.motivation_letter?.content || '',
        improvement: cvGenerated?.motivation_letter?.improvement || '',
      },
    });
  };

  const rollbackCvGenerated = () => {
    if (previousCvGeneratedRef.current) {
      setCvGenerated(previousCvGeneratedRef.current);
      previousCvGeneratedRef.setValue(null);
    }
  };

  const refMessageInterval = useRef<number>(undefined);

  useEffect(() => {
    return () => {
      clearInterval(refMessageInterval.current);
    };
  }, []);

  const updateProfile = (newProfile: ParsedProfileResponse) => {
    setProfile(newProfile);
    const {
      experiencesValues,
      languagesValues,
      formationsValues,
      certificationsValues,
      personalValues,
    } = profileToFormValues(newProfile);
    patchProfile(
      experiencesValues,
      languagesValues,
      formationsValues,
      certificationsValues,
      personalValues
    );
  };

  const { t, i18n } = useTranslation();
  const language = i18n.language === 'fr' ? 'fr' : 'en';
  const ANALYSIS_MESSAGES = [
    t('import-profile.analysis.transferring'),
    t('import-profile.analysis.analyzing_summary'),
    t('import-profile.analysis.analyzing_experiences'),
    t('import-profile.analysis.analyzing_education'),
    t('import-profile.analysis.analyzing_certifications'),
    t('import-profile.analysis.almost_done'),
  ];

  const startLoading = () => {
    setIsLoading(true);
    refMessageInterval.current = setInterval(() => {
      setCurrentMessageIndex(prev => {
        if (prev >= ANALYSIS_MESSAGES.length - 1) {
          clearInterval(refMessageInterval.current);
          return prev;
        }
        return prev + 1;
      });
    }, 6000);
  };

  const stopLoading = () => {
    clearInterval(refMessageInterval.current);
    setIsLoading(false);
    setCurrentMessageIndex(0);
  };

  const generateCVContent = async (data: GenerateContentRequest) => {
    if (!profile?.profile) {
      throw new Error('No profile data available');
    }
    notification.destroy('generate-cv-content-error');
    setContentRequest(data);
    generateAllContentMutation({
      variables: {
        input: {
          jobDescription: data.jobDescription,
          instructions: data.instructions,
          profile: JSON.stringify(profile.profile),
        },
        language,
      },
      onError: error => {
        captureException(error);
        notification.error({
          key: 'generate-cv-content-error',
          message: t('get-offer-details.error'),
          actions: [
            <Button
              key="retry"
              type="default"
              onClick={() => {
                notification.destroy('generate-cv-content-error');
                generateCVContent(data);
              }}
            >
              {t('global.retry')}
            </Button>,
          ],
          duration: 0,
        });
      },
      onCompleted: data => {
        setCvGenerated(data.generateContent);
      },
    });
  };

  const parseCV = async (file: File) => {
    startLoading();
    notification.destroy('parse-cv-error');
    try {
      const { data } = await parseCVMutation({
        variables: {
          file,
          language,
        },
      });

      if (data?.parseCV) {
        updateProfile(data.parseCV);
        // to be sure that the profile is updated before the next step
        await new Promise(resolve => setTimeout(resolve, 500));
        navigate({ to: '/import-profile/edit-profile', search: { method: undefined } });
      } else {
        throw new Error('No profile data available');
      }
    } catch (err) {
      console.error('Failed to parse CV:', err);
      captureException(err);
      notification.error({
        message: t('import-profile.error'),
        duration: 0,
        key: 'parse-cv-error',
        actions: [
          <div className="flex gap-2" key="parse-cv-error-actions">
            <Button
              key="retry"
              type="default"
              onClick={() => {
                notification.destroy('parse-cv-error');
                parseCV(file);
              }}
            >
              {t('global.retry')}
            </Button>
            <Button
              key="fill-from-zero"
              type="primary"
              onClick={() => {
                notification.destroy('parse-cv-error');
                navigate({ to: '/import-profile/edit-profile', search: { method: undefined } });
              }}
            >
              {t('import-profile.fill_from_zero_title')}
            </Button>
          </div>,
        ],
      });
    } finally {
      stopLoading();
    }
  };

  const evaluateCV = async () => {
    startLoading();
    try {
      const { data } = await evaluateCVMutation({
        variables: {
          profile: JSON.stringify(profile),
          language,
        },
      });

      if (data?.evaluateCV) {
        updateProfile(data.evaluateCV);
      }
      return data;
    } catch (e: any) {
      captureException(e);
      notification.error({
        message: t('edit-profile.evaluate_error'),
      });
      return null;
    } finally {
      stopLoading();
    }
  };

  const markRecommendationAsResolved = (recommendationKey: string) => {
    setResolvedRecommendations((prev: string[]) => [...prev, recommendationKey]);
  };

  const updateSectionContent = (config: UpdateSectionConfig & { improvement: string }) => {
    if (!cvGenerated) return;

    const updatedContent = { ...cvGenerated };

    switch (config.type) {
      case ModifySectionType.Summary:
        updatedContent.cv.summary = config.content;
        updatedContent.cv.summary_improvement = config.improvement;
        break;
      case ModifySectionType.MotivationLetter:
        updatedContent.motivation_letter.content = config.content;
        updatedContent.motivation_letter.improvement = config.improvement;
        break;
      case ModifySectionType.Education:
        if (config.index !== undefined && updatedContent.cv.educations[config.index]) {
          updatedContent.cv.educations[config.index].description = config.content;
        }
        break;
      case ModifySectionType.Experience:
        if (config.index !== undefined && updatedContent.cv.experiences[config.index]) {
          updatedContent.cv.experiences[config.index].description = config.content;
        }
        break;
    }

    setCvGenerated(updatedContent);
  };

  const login = (user: AuthUser, token: string) => {
    setAuthUser(user);
    localStorage.setItem('local:token', token);
  };

  const logout = () => {
    // Clear all local storage
    localStorage.clear();
    setAuthUser(null);
    setProfile(null);
    setCvGenerated(null);
    setContentRequest(null);
    setSelectedTemplateId(null);
    setResolvedRecommendations([]);

    // Show notification
    notification.success({
      message: t('auth.logout_success'),
      description: t('auth.logout_success_description'),
    });

    // Redirect to home
    navigate({ to: '/' });
  };

  const { t: translation } = useTranslation();
  const [googleLogin] = useMutation(GOOGLE_LOGIN_MUTATION);

  const handleGoogleSuccess = async (credentialResponse: CredentialResponse) => {
    if (!credentialResponse.credential) {
      captureException(new Error('No credential received from Google'));
      notification.error({
        message: t('auth.login_error'),
        description: t('auth.login_error_description'),
      });
      return;
    }

    try {
      const { data } = await googleLogin({
        variables: {
          token: credentialResponse.credential,
        },
      });

      if (data?.googleLogin) {
        login(data.googleLogin.user, data.googleLogin.jwt);
        updateProfile({
          profile: data.googleLogin.profile,
          content_is_cv: true,
        });
        // notification.success({
        //   message: translation('auth.login_success'),
        //   description: translation('auth.login_success_description'),
        // });
      }
    } catch (error) {
      console.error('Google login failed:', error);
      captureException(error);
      notification.error({
        message: translation('auth.login_error'),
        description: translation('auth.login_error_description'),
      });
    }
  };

  return (
    <OnboardingContext.Provider
      value={{
        profileResponse: profile || null,
        updateProfile,
        parseCV,
        evaluateCV,
        generateCVContent,
        cvGenerated: cvGenerated || null,
        handleCvGeneratedChange,
        rollbackCvGenerated,
        isLoading,
        isGenerating,
        currentMessage: ANALYSIS_MESSAGES[currentMessageIndex],
        resolvedRecommendations,
        markRecommendationAsResolved,
        contentRequest: contentRequest || null,
        setContentRequest,
        updateSectionContent,
        selectedTemplate: selectedTemplate as TemplateViewer,
        setSelectedTemplate,
        authUser,
        login,
        logout,
        handleGoogleSuccess,
      }}
    >
      {children}
    </OnboardingContext.Provider>
  );
}

export function useProfileOnboarding() {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error('useProfileOnboarding must be used within an OnboardingProvider');
  }
  return context;
}
